// Single source of truth for "is this user an admin, and of which orgs?".
//
// Hardening goals (the switcher used to vanish mid-session):
//  1. ONE query per user, shared by every consumer (dedupe in-flight promises).
//  2. Retry with backoff on transient failures instead of resolving to "not admin".
//  3. Persist the last known answer per user id, so a token refresh, tab focus
//     or a brief network blip can never downgrade a resolved admin to null.
//  4. Never treat an error as a negative answer. Only an explicit empty result
//     from a successful query flips someone to "not admin".
import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

export type AdminOrgs = string[];

type Entry = {
  orgs: AdminOrgs | null;      // null = never successfully resolved
  resolvedAt: number;
  inflight: Promise<AdminOrgs | null> | null;
};

const CACHE_PREFIX = 'admin.orgs.v1:';
const FRESH_MS = 60_000;
const RETRIES = 3;

const memory = new Map<string, Entry>();
const listeners = new Set<() => void>();

function notify() {
  listeners.forEach((l) => {
    try { l(); } catch { /* ignore */ }
  });
}

function readPersisted(userId: string): AdminOrgs | null {
  try {
    const raw = sessionStorage.getItem(CACHE_PREFIX + userId);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as AdminOrgs) : null;
  } catch { return null; }
}

function persist(userId: string, orgs: AdminOrgs) {
  try { sessionStorage.setItem(CACHE_PREFIX + userId, JSON.stringify(orgs)); } catch { /* ignore */ }
}

function entryFor(userId: string): Entry {
  let e = memory.get(userId);
  if (!e) {
    e = { orgs: readPersisted(userId), resolvedAt: 0, inflight: null };
    memory.set(userId, e);
  }
  return e;
}

export function getCachedAdminOrgs(userId: string | null): AdminOrgs | null {
  if (!userId) return null;
  return entryFor(userId).orgs;
}

export function clearAdminOrgs(userId?: string) {
  if (userId) {
    memory.delete(userId);
    try { sessionStorage.removeItem(CACHE_PREFIX + userId); } catch { /* ignore */ }
  } else {
    memory.clear();
  }
  notify();
}

async function query(userId: string): Promise<AdminOrgs> {
  let lastError: unknown = null;
  for (let attempt = 0; attempt < RETRIES; attempt += 1) {
    if (attempt > 0) {
      await new Promise((r) => setTimeout(r, 250 * 2 ** (attempt - 1)));
    }
    const { data, error } = await supabase
      .from('pulse_org_members')
      .select('org_id')
      .eq('user_id', userId)
      .eq('role', 'admin');
    if (!error) {
      return (data ?? []).map((r) => r.org_id).filter(Boolean) as string[];
    }
    lastError = error;
  }
  throw lastError ?? new Error('admin lookup failed');
}

/** Resolve admin orgs for a user. Shared + deduped + retried. */
export function loadAdminOrgs(userId: string, opts?: { force?: boolean }): Promise<AdminOrgs | null> {
  const e = entryFor(userId);
  if (e.inflight) return e.inflight;
  if (!opts?.force && e.orgs !== null && Date.now() - e.resolvedAt < FRESH_MS) {
    return Promise.resolve(e.orgs);
  }
  e.inflight = query(userId)
    .then((orgs) => {
      e.orgs = orgs;
      e.resolvedAt = Date.now();
      persist(userId, orgs);
      notify();
      return orgs;
    })
    .catch(() => {
      // Keep whatever we already knew. Never downgrade on failure.
      notify();
      return e.orgs;
    })
    .finally(() => { e.inflight = null; });
  return e.inflight;
}

type Status = 'loading' | 'ready' | 'not_admin' | 'error';

export function useAdminOrgs(userId: string | null): { orgs: AdminOrgs | null; status: Status } {
  const [, force] = useState(0);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    const l = () => force((n) => n + 1);
    listeners.add(l);
    return () => { listeners.delete(l); };
  }, []);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    setErrored(false);
    loadAdminOrgs(userId).then((orgs) => {
      if (!cancelled && orgs === null) setErrored(true);
    });
    return () => { cancelled = true; };
  }, [userId]);

  // Re-validate quietly when the tab regains focus, without clearing state.
  useEffect(() => {
    if (!userId) return;
    const onFocus = () => { loadAdminOrgs(userId, { force: true }); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus); 
  }, [userId]);

  const orgs = userId ? entryFor(userId).orgs : null;
  const status: Status = !userId
    ? 'loading'
    : orgs === null
      ? (errored ? 'error' : 'loading')
      : orgs.length > 0
        ? 'ready'
        : 'not_admin';

  return { orgs, status };
}
